import React, { useState } from 'react'

import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    makeStyles,
    Popover,
    Theme,
    Tooltip,
} from '@material-ui/core'
import { Edit } from '@material-ui/icons'
import CloseIcon from '@material-ui/icons/Close'
import FlagIcon from '@material-ui/icons/Flag'
import SaveIcon from '@material-ui/icons/Save'

import { Container, Selector } from '../../../components'
import { EditStatusPopover } from '../../../components/EditStatusPopover/EditStatusPopover'
import { CustomStatus, InventoryConfig, StatusGroup } from '../../../models'
import { usePopover } from '../../../hooks/usePopover'
import { axiosInstance } from '../../../helpers'
import { toast } from 'react-toastify'
import { setInventoryConfigList } from '../../../store'
import { useAppDispatch, useAptConfig } from '../../../hooks'
import { StatusGroupForm } from '../../StatusGroupConfig/StatusGroupSideDrawer'

const useStyles = makeStyles<Theme, Props>((theme) => ({
    statusContainer: {
        marginTop: theme.spacing(2),
        display: 'flex',
        width: '100%',
        position: 'relative',
        padding: theme.spacing(1),
        '&:hover': {
            border: (props) =>
                props.hasEditPermission
                    ? `1px solid ${theme.palette.grey[400]}`
                    : 'none',
            cursor: (props) =>
                props.hasEditPermission ? 'pointer' : 'default',
            '& $editButton': {
                opacity: 1,
            },
        },
    },
    editButton: {
        opacity: 0,
        transition: 'opacity 0.2s',
    },
}))

interface Props {
    inventoryConfig: InventoryConfig
    inventoryConfigList: InventoryConfig[]
    theme: Theme
    statusGroupList: StatusGroup[]
    hasEditPermission: boolean
    updateInventoryConfig: (
        inventoryConfig: InventoryConfig,
        keyValuePairs: { key: string; value: string }[],
    ) => void
}

export const StatusGroupDisplay = (props: Props) => {
    const {
        inventoryConfig,
        theme,
        hasEditPermission,
        inventoryConfigList,
        updateInventoryConfig,
    } = props

    const dispatch = useAppDispatch()

    const { updateCustomStatusReq } = useAptConfig({})

    const classes = useStyles(props)

    const [statusGroupId, setStatusGroupId] = useState(
        inventoryConfig.status_group?.id ?? -1,
    )

    const { isOpen, anchorEl, handleOpen, handleClose } = usePopover()
    const {
        isOpen: isEditStatusOpen,
        anchorEl: editStatusAnchorEl,
        handleOpen: handleEditStatusOpen,
        handleClose: handleEditStatusClose,
    } = usePopover()

    const [editingStatus, setEditingStatus] = useState<CustomStatus | null>(
        null,
    )
    const [statusName, setStatusName] = useState('')
    const [statusColor, setStatusColor] = useState('#000000')
    const [statusCausesFlag, setStatusCausesFlag] = useState(false)

    const [showStatusGroupForm, setShowStatusGroupForm] = useState(false)
    const [editingStatusGroup, setEditingStatusGroup] = useState<
        StatusGroup | undefined
    >(undefined)

    const defaultStatus = inventoryConfig.status_group?.default_status

    const handleEditStatusClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        status: CustomStatus,
    ) => {
        setEditingStatus(status)
        setStatusName(status.name)
        setStatusColor(status.color)
        setStatusCausesFlag(status.causes_flag)
        handleEditStatusOpen(event)
    }

    const handleSaveStatus = (
        name: string,
        color: string,
        causesFlag: boolean,
    ) => {
        if (!editingStatus) return

        // Update the actual status and the status list
        updateCustomStatusReq({
            id: editingStatus.id,
            body: {
                name: name,
                color: color,
                causes_flag: causesFlag,
            },
        })
            .then((updatedStatus) => {
                toast.success('Status updated successfully')
                handleEditStatusClose()
            })
            .catch((e) => {
                console.log('error', e)
                toast.error(e.response.data.message)
            })
    }

    return (
        <>
            <Container className={classes.statusContainer}>
                <Container
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        flex: 1,
                    }}
                >
                    {inventoryConfig.custom_status_list?.map((status) => {
                        const isDefaultStatus = status.id === defaultStatus?.id

                        return (
                            <div
                                key={`inv_conf_${inventoryConfig.id}_status_${status.id}`}
                                style={{
                                    padding: theme.spacing(1, 2),
                                    border: `1px solid black`,
                                    borderRadius: theme.shape.borderRadius,
                                    margin: theme.spacing(1),
                                    width: `calc((100% / 3) - ${theme.spacing(
                                        2,
                                    )}px)`,
                                    boxSizing: 'border-box',
                                    borderColor: isDefaultStatus ? '' : 'black',
                                    backgroundColor: isDefaultStatus
                                        ? status.color
                                        : 'white',
                                    color: isDefaultStatus ? 'white' : 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    position: 'relative',
                                }}
                                onMouseEnter={(e) => {
                                    const editIcon = e.currentTarget.querySelector(
                                        '.status-edit-icon',
                                    ) as HTMLElement
                                    if (editIcon) {
                                        editIcon.style.opacity = '1'
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    const editIcon = e.currentTarget.querySelector(
                                        '.status-edit-icon',
                                    ) as HTMLElement
                                    if (editIcon) {
                                        editIcon.style.opacity = '0'
                                    }
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flex: 1,
                                    }}
                                >
                                    <span
                                        style={{
                                            textAlign: 'center',
                                            alignSelf: 'center',
                                        }}
                                    >
                                        {status.name}
                                    </span>
                                    {status.causes_flag ? (
                                        <Tooltip title="Causes Flag">
                                            <FlagIcon
                                                style={{
                                                    width: '17px',
                                                    height: '17px',
                                                    marginLeft: theme.spacing(
                                                        0.5,
                                                    ),
                                                }}
                                            />
                                        </Tooltip>
                                    ) : null}
                                </div>
                                <Tooltip title="Edit Status">
                                    <IconButton
                                        className="status-edit-icon"
                                        size="small"
                                        style={{
                                            opacity: 0,
                                            transition:
                                                'opacity 0.2s ease-in-out',
                                            marginLeft: theme.spacing(1),
                                            padding: theme.spacing(0.5),
                                        }}
                                        onClick={(e) =>
                                            handleEditStatusClick(e, status)
                                        }
                                    >
                                        <Edit
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        )
                    })}
                </Container>
                {hasEditPermission && (
                    <Container
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Tooltip title="Change Status Group">
                            <IconButton
                                // Temporarily apply styles directly for testing
                                className={classes.editButton} // Ensure this is applied
                                size="small"
                                onClick={handleOpen}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Container>
                )}
                <Popover
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    open={isOpen}
                    PaperProps={{
                        style: {
                            borderRadius: theme.shape.borderRadius * 2,
                            boxShadow: theme.shadows[8],
                        },
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'column',
                            padding: theme.spacing(2.5),
                            minWidth: '340px',
                        }}
                    >
                        <Container
                            style={{
                                fontWeight: 700,
                                fontSize: '18px',
                                marginBottom: theme.spacing(2.5),
                                color: theme.palette.text.primary,
                            }}
                        >
                            Change Status Group
                        </Container>

                        <Container style={{ marginBottom: theme.spacing(2.5) }}>
                            <Selector
                                label="Status Group"
                                currentValue={statusGroupId}
                                data={props.statusGroupList ?? []}
                                customStyle={{
                                    formControl: { margin: 0, width: '100%' },
                                }}
                                onChange={(
                                    event: React.ChangeEvent<{
                                        value: unknown
                                    }>,
                                ) => {
                                    const newDefaultGroup = props.statusGroupList?.find(
                                        (s) =>
                                            s.id ===
                                            (event.target.value as number),
                                    )
                                    setStatusGroupId(newDefaultGroup?.id ?? -1)
                                }}
                                getDisplayString={(s: StatusGroup) =>
                                    `${s.name}`
                                }
                                searchable={true}
                            />
                        </Container>

                        <Container
                            style={{
                                display: 'flex',
                                gap: theme.spacing(1.5),
                                marginBottom: theme.spacing(2.5),
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    const selectedGroup = props.statusGroupList?.find(
                                        (sg) => sg.id === statusGroupId,
                                    )
                                    if (selectedGroup) {
                                        setEditingStatusGroup(selectedGroup)
                                        setShowStatusGroupForm(true)
                                        handleClose()
                                    }
                                }}
                                disabled={statusGroupId === -1}
                                style={{
                                    textTransform: 'none',
                                    width: 145,
                                    borderRadius: 8,
                                    borderColor: theme.palette.grey[300],
                                    color: theme.palette.text.primary,
                                    padding: theme.spacing(1, 2),
                                    fontWeight: 500,
                                }}
                                startIcon={<Edit style={{ fontSize: 18 }} />}
                            >
                                Edit
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setEditingStatusGroup(undefined)
                                    setShowStatusGroupForm(true)
                                    handleClose()
                                }}
                                style={{
                                    textTransform: 'none',
                                    width: 145,
                                    borderRadius: 8,
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    padding: theme.spacing(1, 2),
                                    fontWeight: 500,
                                    boxShadow: 'none',
                                }}
                            >
                                + Create New
                            </Button>
                        </Container>

                        <Divider style={{ marginBottom: theme.spacing(2) }} />

                        <Container
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: theme.spacing(1.5),
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={handleClose}
                                style={{
                                    textTransform: 'none',
                                    width: 145,
                                    borderRadius: 8,
                                    borderColor: theme.palette.grey[300],
                                    color: theme.palette.text.secondary,
                                    padding: theme.spacing(1, 2),
                                    fontWeight: 500,
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    updateInventoryConfig(inventoryConfig, [
                                        {
                                            key: 'status_group',
                                            value: statusGroupId.toString(),
                                        },
                                    ])
                                    handleClose()
                                }}
                                disabled={statusGroupId === -1}
                                style={{
                                    textTransform: 'none',
                                    width: 145,
                                    borderRadius: 8,
                                    backgroundColor: theme.palette.primary.dark,
                                    color: 'white',
                                    padding: theme.spacing(1, 2),
                                    fontWeight: 600,
                                    boxShadow: 'none',
                                }}
                            >
                                Save Changes
                            </Button>
                        </Container>
                    </Container>
                </Popover>

                {/* Edit Status Popover */}
                <EditStatusPopover
                    open={isEditStatusOpen}
                    anchorEl={editStatusAnchorEl}
                    onClose={handleEditStatusClose}
                    statusName={statusName}
                    statusColor={statusColor}
                    statusCausesFlag={statusCausesFlag}
                    onSave={handleSaveStatus}
                    theme={theme}
                />
            </Container>

            {/* Status Group Form Dialog */}
            <Dialog
                open={showStatusGroupForm}
                onClose={() => {
                    setShowStatusGroupForm(false)
                }}
                TransitionProps={{
                    onExited: () => {
                        // Clear editing state only after dialog has fully closed
                        setEditingStatusGroup(undefined)
                    },
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: theme.shape.borderRadius * 3,
                        maxHeight: '90vh',
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle
                    style={{
                        padding: theme.spacing(2, 3),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                    }}
                >
                    <Container
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <span
                            style={{
                                fontWeight: 600,
                                fontSize: '1.25rem',
                                color: theme.palette.text.primary,
                            }}
                        >
                            {editingStatusGroup
                                ? 'Edit Status Group'
                                : 'Create Status Group'}
                        </span>
                        <IconButton
                            size="small"
                            onClick={() => {
                                setShowStatusGroupForm(false)
                            }}
                            style={{
                                color: theme.palette.text.secondary,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Container>
                </DialogTitle>
                <DialogContent
                    style={{
                        padding: theme.spacing(3),
                        overflowY: 'auto',
                    }}
                >
                    <StatusGroupForm
                        handleClose={() => {
                            setShowStatusGroupForm(false)
                        }}
                        statusGroup={editingStatusGroup}
                        modalMode={true}
                        setSelectedStatusGroup={(
                            newStatusGroup: StatusGroup,
                        ) => {
                            // Update the inventory config with the new status group
                            updateInventoryConfig(inventoryConfig, [
                                {
                                    key: 'status_group',
                                    value: newStatusGroup.id.toString(),
                                },
                            ])

                            // Update the inventory config's custom_status_list in Redux
                            // Map custom_statuses to custom_status_list (StatusWithCost format)
                            const updatedCustomStatusList =
                                newStatusGroup.custom_statuses?.map((s) => ({
                                    ...s,
                                    cost:
                                        inventoryConfig.custom_status_list?.find(
                                            (cs) => cs.id === s.id,
                                        )?.cost ?? 0,
                                })) ?? []

                            // Update the inventory config in the list
                            const updatedInventoryConfigList = inventoryConfigList.map(
                                (config) => {
                                    if (config.id === inventoryConfig.id) {
                                        return {
                                            ...config,
                                            status_group: newStatusGroup,
                                            custom_status_list: updatedCustomStatusList,
                                        }
                                    }
                                    return config
                                },
                            )

                            dispatch(
                                setInventoryConfigList(
                                    updatedInventoryConfigList,
                                ),
                            )

                            // Close the form
                            setShowStatusGroupForm(false)
                        }}
                        hideHeader={true}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}
